// Linear OAuth2 — Authorization Code + PKCE
//
// Offline-first notes:
//   - Linear access tokens do NOT expire (permanent until revoked)
//   - Token stored in IndexedDB, survives app restarts
//   - When offline, cached data is served; no refresh needed
//
// OAuth App requirements (user registers their own Linear OAuth App):
//   - client_id     — from Linear settings > API > OAuth Applications
//   - client_secret — from the same app
//   - redirect_uri  — verigit://oauth/callback (Electron) or <origin>/oauth/callback
//
// Endpoints:
//   Auth URL  : https://linear.app/oauth/authorize
//   Token URL : https://api.linear.app/oauth/token
//   User query: https://api.linear.app/graphql  (viewer { id name email })
//
// Default scopes: read write

import {
  generateRandomString,
  generateCodeChallenge,
  isElectron,
  proxyUrl,
  parseCallbackUrl,
  openElectronOAuth2Popup,
  openBrowserOAuth2Popup,
  getOAuth2RedirectUri,
  EXPIRY_BUFFER_MS,
} from './platform-oauth2';
import type { PlatformOAuth2Token, PlatformOAuthState } from './types';
import {
  getPlatformOAuth2Token,
  savePlatformOAuth2Token,
  clearPlatformOAuth2Token,
} from './storage';

const LINEAR_AUTH_URL = 'https://linear.app/oauth/authorize';
const LINEAR_TOKEN_URL = 'https://api.linear.app/oauth/token';
const LINEAR_GRAPHQL_URL = 'https://api.linear.app/graphql';

export const LINEAR_OAUTH_SCOPES = 'read write';

const PLATFORM = 'linear' as const;

// ---------------------------------------------------------------------------
// LinearOAuth2Service
// ---------------------------------------------------------------------------

class LinearOAuth2Service {
  private static instance: LinearOAuth2Service;
  private clientId = '';
  private clientSecret = '';
  private redirectUri = '';
  private stateCallbacks: Set<(state: PlatformOAuthState) => void> = new Set();

  private constructor() {}

  static getInstance(): LinearOAuth2Service {
    if (!LinearOAuth2Service.instance) {
      LinearOAuth2Service.instance = new LinearOAuth2Service();
    }
    return LinearOAuth2Service.instance;
  }

  configure(clientId: string, clientSecret: string, redirectUri?: string): void {
    this.clientId = clientId;
    this.clientSecret = clientSecret;
    this.redirectUri = redirectUri ?? getOAuth2RedirectUri();
    console.log('[LinearOAuth2] Configured', { clientId, redirectUri: this.redirectUri });
  }

  onStateChange(cb: (state: PlatformOAuthState) => void): () => void {
    this.stateCallbacks.add(cb);
    return () => this.stateCallbacks.delete(cb);
  }

  private notifyStateChange(state: PlatformOAuthState): void {
    this.stateCallbacks.forEach((cb) => {
      try { cb(state); } catch (e) { console.error('[LinearOAuth2] State callback error:', e); }
    });
  }

  private computeState(token: PlatformOAuth2Token | null): PlatformOAuthState {
    if (!token) {
      return {
        isAuthenticated: false,
        isExpired: false,
        canRefresh: false,
        isOfflineMode: !navigator.onLine,
        expiresInMs: 0,
      };
    }
    // Linear tokens don't expire (expiresAt === 0)
    const now = Date.now();
    const expiresInMs =
      token.expiresAt === 0 ? Number.POSITIVE_INFINITY : token.expiresAt - now - EXPIRY_BUFFER_MS;
    return {
      isAuthenticated: true,
      isExpired: token.expiresAt !== 0 && expiresInMs <= 0,
      canRefresh: !!token.refreshToken,
      isOfflineMode: !navigator.onLine,
      expiresInMs,
      token,
    };
  }

  async getState(): Promise<PlatformOAuthState> {
    const token = await getPlatformOAuth2Token(PLATFORM);
    return this.computeState(token);
  }

  // ---------------------------------------------------------------------------
  // Auth flow
  // ---------------------------------------------------------------------------

  private async buildAuthUrl(): Promise<{ url: string; state: string; verifier: string }> {
    if (!this.clientId) {
      throw new Error(
        'LinearOAuth2Service not configured — call configure() first with your Linear OAuth App credentials'
      );
    }
    const stateParam = generateRandomString(32);
    const verifier = generateRandomString(64);
    const { challenge, method } = await generateCodeChallenge(verifier);

    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      response_type: 'code',
      scope: LINEAR_OAUTH_SCOPES,
      state: stateParam,
      code_challenge: challenge,
      code_challenge_method: method,
      actor: 'application',
    });
    return {
      url: `${LINEAR_AUTH_URL}?${params.toString()}`,
      state: stateParam,
      verifier,
    };
  }

  private async exchangeCodeForTokens(
    code: string,
    verifier: string
  ): Promise<PlatformOAuth2Token> {
    console.log('[LinearOAuth2] Exchanging code for tokens...');

    const body = new URLSearchParams({
      client_id: this.clientId,
      client_secret: this.clientSecret,
      code,
      redirect_uri: this.redirectUri,
      grant_type: 'authorization_code',
      code_verifier: verifier,
    });

    const fetchUrl = isElectron ? LINEAR_TOKEN_URL : proxyUrl(LINEAR_TOKEN_URL);
    const response = await fetch(fetchUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Linear token exchange failed (${response.status}): ${text}`);
    }

    const data = await response.json();
    if (data.error) {
      throw new Error(`Linear OAuth error: ${data.error_description ?? data.error}`);
    }

    const now = Date.now();
    let userId: string | undefined;
    let displayName: string | undefined;
    let email: string | undefined;

    try {
      const gqlFetchUrl = isElectron ? LINEAR_GRAPHQL_URL : proxyUrl(LINEAR_GRAPHQL_URL);
      const viewerResp = await fetch(gqlFetchUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${data.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: 'query { viewer { id name email } }' }),
      });
      if (viewerResp.ok) {
        const viewerData = await viewerResp.json();
        const viewer = viewerData.data?.viewer;
        userId = viewer?.id;
        displayName = viewer?.name;
        email = viewer?.email;
        console.log('[LinearOAuth2] User profile:', { userId, displayName, email });
      }
    } catch (e) {
      console.warn('[LinearOAuth2] Could not fetch viewer info:', e);
    }

    // Linear tokens don't expire — set expiresAt to 0
    const token: PlatformOAuth2Token = {
      id: `${PLATFORM}-oauth2`,
      platform: PLATFORM,
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresAt: data.expires_in ? now + data.expires_in * 1000 : 0,
      scopes: (data.scope ?? LINEAR_OAUTH_SCOPES).split(' '),
      tokenType: data.token_type ?? 'Bearer',
      userId,
      displayName,
      email,
      issuedAt: now,
      offlineCapable: true,
    };

    await savePlatformOAuth2Token(token);
    this.notifyStateChange(this.computeState(token));
    console.log('[LinearOAuth2] Token saved. ExpiresAt:', token.expiresAt === 0 ? 'never' : new Date(token.expiresAt).toISOString());
    return token;
  }

  async initiateFlow(): Promise<PlatformOAuth2Token> {
    const { url, state, verifier } = await this.buildAuthUrl();
    let callbackUrl: string;
    if (isElectron) {
      callbackUrl = await openElectronOAuth2Popup(url, this.redirectUri);
    } else {
      callbackUrl = await openBrowserOAuth2Popup(url, 'linear-oauth2-callback');
    }
    const { code } = parseCallbackUrl(callbackUrl, state);
    return this.exchangeCodeForTokens(code, verifier);
  }

  // ---------------------------------------------------------------------------
  // Token access
  // ---------------------------------------------------------------------------

  /**
   * Returns the current access token.
   * Linear tokens don't expire, so no refresh logic is needed.
   */
  async getAccessToken(): Promise<string | null> {
    const token = await getPlatformOAuth2Token(PLATFORM);
    return token?.accessToken ?? null;
  }

  async revokeToken(): Promise<void> {
    await clearPlatformOAuth2Token(PLATFORM);
    this.notifyStateChange(this.computeState(null));
    console.log('[LinearOAuth2] Token revoked and cleared from storage');
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const linearOAuth2 = LinearOAuth2Service.getInstance();
